/*
  ZEVQILO Atelier — scroll-showcase.js
  <scroll-showcase>: on large screens with motion on (Theme settings → Motion → Rich), the product rail
  inside stays in place while the page scrolls and the products slide sideways. Phones, tablets and
  visitors who prefer reduced motion keep the normal swipeable carousel. Tabbing to a product scrolls
  the page so that product is in view.

  This script measures how far the row travels. Browsers with scroll-driven animations then move it
  themselves, on the compositor and in step with the scroll (sections/product-rail.liquid); in the
  others the script follows the scroll.
*/
(() => {
  if (customElements.get('scroll-showcase')) return;

  /* Wide and tall enough for the row to fit below the header: short windows keep the carousel. */
  const QUERY = '(min-width: 1024px) and (min-height: 600px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)';
  const MIN_DISTANCE = 160;
  const CSS_DRIVEN = Boolean(window.CSS?.supports?.('animation-timeline: view()'));

  class ScrollShowcase extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.showcase__frame');
      this.track = this.querySelector('[data-rail-track]');
      if (!this.frame || !this.track) return;
      this.query = window.matchMedia(QUERY);
      this.pinned = false;
      this.distance = 0;
      this.update = this.update.bind(this);
      this.setup = this.setup.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.requestUpdate = () => {
        if (!this.frameId) this.frameId = window.requestAnimationFrame(this.update);
      };
      this.onResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.measure(), 150);
      };
      this.query.addEventListener('change', this.setup);
      this.addEventListener('focusin', this.onFocus);
      this.setup();
    }

    disconnectedCallback() {
      this.query?.removeEventListener('change', this.setup);
      this.removeEventListener('focusin', this.onFocus);
      window.removeEventListener('scroll', this.requestUpdate);
      window.removeEventListener('resize', this.onResize);
      window.cancelAnimationFrame(this.frameId);
      window.clearTimeout(this.resizeTimer);
    }

    setup() {
      const enable = this.query.matches && document.documentElement.classList.contains('motion-rich');
      this.pinned = enable;
      this.toggleAttribute('data-pinned', enable);
      window.removeEventListener('scroll', this.requestUpdate);
      window.removeEventListener('resize', this.onResize);
      if (enable) {
        if (!CSS_DRIVEN) window.addEventListener('scroll', this.requestUpdate, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });
        this.measure();
      } else {
        this.reset();
      }
    }

    reset() {
      this.distance = 0;
      this.track.style.transform = '';
      this.style.removeProperty('--showcase-distance');
      this.style.removeProperty('--showcase-progress');
    }

    /* How far the row has to travel: from its first product to its last one plus the end padding. */
    measure() {
      if (!this.pinned) return;
      this.toggleAttribute('data-pinned', true);
      this.track.style.transform = '';
      const last = this.track.lastElementChild;
      const end = last ? last.offsetLeft + last.offsetWidth + (parseFloat(getComputedStyle(this.track).paddingRight) || 0) : 0;
      const distance = Math.round(end - this.track.clientWidth);
      if (distance < MIN_DISTANCE) {
        /* Too few products to be worth pinning: stay a plain row. */
        this.toggleAttribute('data-pinned', false);
        this.reset();
        return;
      }
      this.distance = distance;
      this.style.setProperty('--showcase-distance', `${distance}px`);
      this.update();
    }

    /* Script-driven fallback: place the row for the current scroll position. */
    update() {
      this.frameId = 0;
      if (CSS_DRIVEN || !this.pinned || !this.distance) return;
      const progress = Math.min(1, Math.max(0, -this.getBoundingClientRect().top / this.distance));
      this.track.style.transform = `translate3d(${(-progress * this.distance).toFixed(1)}px, 0, 0)`;
      this.style.setProperty('--showcase-progress', progress.toFixed(4));
    }

    onFocus(event) {
      if (!this.pinned || !this.distance) return;
      const item = event.target.closest('[data-rail-track] > *');
      if (!item) return;
      const offset = Math.min(this.distance, Math.max(0, item.offsetLeft + item.offsetWidth / 2 - this.track.clientWidth / 2));
      window.scrollTo({ top: window.scrollY + this.getBoundingClientRect().top + offset, behavior: 'instant' });
      this.update();
    }
  }

  customElements.define('scroll-showcase', ScrollShowcase);
})();
